/**
 * 剪辑（P-D⑥，蓝图 §4「云端工程列表 → 时间线编辑器」+ §6.2 导出剪映/FCPXML）。
 *
 * 自研轻量时间线（不引重型开源引擎，依赖可控、只做粗排/拼接/字幕/配音，符合混合架构）。
 * 这里放工程/轨道/片段的类型 + 时间线时长纯函数（可测）+ mock 种子；
 * 真实合成/导出（剪映草稿 / FCPXML）+ ffmpeg 落接真后端阶段。
 */

export type ClipKind = 'video' | 'audio' | 'subtitle';

export interface Clip {
  id: string;
  kind: ClipKind;
  name: string;
  /** 起始秒 */
  start: number;
  /** 时长秒 */
  dur: number;
}

export interface Track {
  id: string;
  kind: ClipKind;
  name: string;
  clips: Clip[];
}

export interface EditProject {
  id: string;
  name: string;
  updatedAt: string;
  tracks: Track[];
}

const CLIP_COLOR: Record<ClipKind, string> = { video: 'gc-blue', audio: 'gc-green', subtitle: 'gc-amber' };
/** 片段配色 token 键（实色在 index.css 的 .gc-*）。 */
export function clipColor(kind: ClipKind): string {
  return CLIP_COLOR[kind];
}

/** 时间线总时长 = 各轨道最右片段结束秒的最大值。纯函数可测。 */
export function timelineDuration(tracks: Track[]): number {
  let end = 0;
  for (const t of tracks) {
    for (const c of t.clips) {
      end = Math.max(end, c.start + c.dur);
    }
  }
  return end;
}

/* ---------- 时间线编辑纯操作（可测；P-D⑥ 增强） ---------- */

/** 吸附：给定候选点集与阈值，返回阈值内最近的点，否则原值。 */
export function snapTime(t: number, points: number[], threshold: number): number {
  let best = t;
  let bestDist = threshold;
  for (const p of points) {
    const d = Math.abs(p - t);
    if (d <= bestDist) {
      bestDist = d;
      best = p;
    }
  }
  return best;
}

/** 移动片段起点（不负）。 */
export function moveClip(clips: Clip[], id: string, newStart: number): Clip[] {
  return clips.map((c) => (c.id === id ? { ...c, start: Math.max(0, newStart) } : c));
}

/** 删除片段。 */
export function removeClip(clips: Clip[], id: string): Clip[] {
  return clips.filter((c) => c.id !== id);
}

/** 某类轨道的末尾秒（无片段则 0），用于“追加快照到轨尾”。 */
export function trackEnd(tracks: Track[], kind: ClipKind): number {
  let end = 0;
  for (const t of tracks) {
    if (t.kind !== kind) continue;
    for (const c of t.clips) end = Math.max(end, c.start + c.dur);
  }
  return end;
}

/** 时间线缩放自适应刻度步长（秒）：像素越密步长越小。 */
export function tickStep(pxPerSec: number): number {
  if (pxPerSec >= 120) return 1;
  if (pxPerSec >= 60) return 2;
  if (pxPerSec >= 30) return 5;
  return 10;
}

/** 在 at 处分割片段（仅当 at 严格落在片段内部）；新片段 id 加后缀。 */
export function splitClip(clips: Clip[], id: string, at: number, newId: string): Clip[] {
  const out: Clip[] = [];
  for (const c of clips) {
    if (c.id === id && at > c.start + 0.05 && at < c.start + c.dur - 0.05) {
      const left = at - c.start;
      out.push({ ...c, dur: left });
      out.push({ ...c, id: newId, start: at, dur: c.dur - left });
    } else {
      out.push(c);
    }
  }
  return out;
}

/* ---------- 临时保存（本地草稿，不上传） ---------- */

function draftKey(projectId: string): string {
  return `videoai-pro.editing.draft.${projectId}`;
}
export function saveDraft(projectId: string, tracks: Track[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(draftKey(projectId), JSON.stringify(tracks));
  } catch {
    /* 忽略配额异常 */
  }
}
export function loadDraft(projectId: string): Track[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(draftKey(projectId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Track[]) : null;
  } catch {
    return null;
  }
}

/* ---------- 导出剪映草稿 + 从分镜构建工程（D-14） ---------- */

/** 导出为（简化版）剪映草稿 JSON 结构；真实媒体路径/完整字段接后端时补齐。 */
export function toJianyingDraft(project: EditProject): Record<string, unknown> {
  return {
    draft_version: '5.9.0',
    name: project.name,
    duration: timelineDuration(project.tracks) * 1_000_000, // 微秒
    tracks: project.tracks.map((t) => ({
      type: t.kind,
      name: t.name,
      segments: t.clips.map((c) => ({
        path: c.name, // 占位：真媒体路径待接后端
        in: c.start * 1_000_000,
        out: (c.start + c.dur) * 1_000_000,
      })),
    })),
  };
}

/** 浏览器下载 JSON（无 window/document 则静默）。 */
export function downloadJson(filename: string, data: unknown): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** 从分镜序列构建一个剪辑工程（画面轨按顺序排片），用于“故事板/画布→剪辑”链路。 */
export function buildProjectFromShots(
  id: string,
  name: string,
  shots: { name: string; dur: number }[],
): EditProject {
  let t = 0;
  const clips: Clip[] = shots.map((s, i) => {
    const clip: Clip = { id: `${id}-c${i}`, kind: 'video', name: s.name, start: t, dur: Math.max(1, s.dur) };
    t += clip.dur;
    return clip;
  });
  return {
    id,
    name,
    updatedAt: new Date().toISOString(),
    tracks: [
      { id: `${id}-v`, kind: 'video', name: '画面', clips },
      { id: `${id}-a`, kind: 'audio', name: '配音', clips: [] },
      { id: `${id}-s`, kind: 'subtitle', name: '字幕', clips: [] },
    ],
  };
}

export const MOCK_PROJECTS: EditProject[] = [
  {
    id: 'ep1',
    name: '《长夜将尽》第 1 集 · 粗剪',
    updatedAt: '2026-10-06T02:00:00.000Z',
    tracks: [
      { id: 'tr-v', kind: 'video', name: '画面', clips: [
        { id: 'c1', kind: 'video', name: '镜1', start: 0, dur: 5 },
        { id: 'c2', kind: 'video', name: '镜2', start: 5, dur: 5 },
        { id: 'c3', kind: 'video', name: '镜3', start: 10, dur: 8 },
      ] },
      { id: 'tr-a', kind: 'audio', name: '配音', clips: [
        { id: 'a1', kind: 'audio', name: '旁白', start: 0, dur: 6 },
        { id: 'a2', kind: 'audio', name: '对白·林晚', start: 6, dur: 7 },
      ] },
      { id: 'tr-s', kind: 'subtitle', name: '字幕', clips: [
        { id: 's1', kind: 'subtitle', name: '那晚的雨…', start: 0, dur: 6 },
      ] },
    ],
  },
  {
    id: 'ep2',
    name: '《长夜将尽》第 2 集 · 待剪',
    updatedAt: '2026-10-05T09:00:00.000Z',
    tracks: [
      { id: 'tr-v2', kind: 'video', name: '画面', clips: [{ id: 'c1', kind: 'video', name: '镜1', start: 0, dur: 5 }] },
    ],
  },
];
